import React from 'react';
import { Link } from 'react-router-dom';
import {
    CheckCircle2,
    ArrowRight,
    Wallet,
    Landmark,
    CreditCard,
    LineChart,
    ShieldCheck,
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const BankingIndustryLayout = ({ industry }) => {
    return (
        <div className="min-h-screen bg-white">

            {/* ================= HERO ================= */}
            <section
                className="relative bg-cover bg-center text-white py-32"
                style={{
                    backgroundImage:
                        "linear-gradient(rgba(30,27,75,0.8), rgba(88,28,135,0.8)), url('/images/industries/banking/hero.jpg')",
                }}
            >
                <div className="max-w-7xl mx-auto px-6">
                    <h1 className="text-5xl md:text-6xl font-bold mb-6">
                        {industry.name}
                    </h1>
                    <p className="text-xl text-indigo-100 max-w-3xl">
                        {industry.description}
                    </p>
                </div>
            </section>

            {/* ================= RETAIL BANKING ================= */}
            <section className="py-24 max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-14 items-center">
                <div>
                    <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
                        <Wallet className="text-indigo-600" /> Retail Banking
                    </h2>
                    <p className="text-gray-700 mb-6">
                        Create digital-first retail banking experiences with personalized
                        journeys, omnichannel engagement, and real-time insights.
                    </p>
                    <ul className="space-y-3">
                        <li className="flex gap-2"><CheckCircle2 className="text-green-600" /> Digital onboarding</li>
                        <li className="flex gap-2"><CheckCircle2 className="text-green-600" /> Customer 360 platforms</li>
                        <li className="flex gap-2"><CheckCircle2 className="text-green-600" /> Personalized offers</li>
                    </ul>
                </div>
                <img
                    src="/images/industries/banking/retail.png"
                    alt="Retail Banking"
                    className="rounded-2xl shadow-lg"
                />
            </section>

            {/* ================= CORPORATE BANKING ================= */}
            <section className="py-24 bg-gray-50">
                <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-14 items-center">
                    <img
                        src="/images/industries/banking/corporate.jpg"
                        alt="Corporate Banking"
                        className="rounded-2xl shadow-lg"
                    />
                    <div>
                        <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
                            <Landmark className="text-indigo-600" /> Corporate Banking
                        </h2>
                        <p className="text-gray-700 mb-6">
                            Enable large-scale corporate banking operations with secure,
                            scalable platforms and automated trade finance solutions.
                        </p>
                        <ul className="space-y-3">
                            <li className="flex gap-2"><CheckCircle2 className="text-green-600" /> Treasury modernization</li>
                            <li className="flex gap-2"><CheckCircle2 className="text-green-600" /> Trade finance automation</li>
                            <li className="flex gap-2"><CheckCircle2 className="text-green-600" /> Risk & compliance platforms</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* ================= PAYMENTS ================= */}
            <section className="py-24 max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-14 items-center">
                <div>
                    <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
                        <CreditCard className="text-indigo-600" /> Payments & Cards
                    </h2>
                    <p className="text-gray-700 mb-6">
                        Modernize payments infrastructure to support real-time,
                        cross-border, and digital payment ecosystems.
                    </p>
                    <ul className="space-y-3">
                        <li className="flex gap-2"><CheckCircle2 className="text-green-600" /> Real-time payments</li>
                        <li className="flex gap-2"><CheckCircle2 className="text-green-600" /> Fraud detection</li>
                        <li className="flex gap-2"><CheckCircle2 className="text-green-600" /> Card lifecycle systems</li>
                    </ul>
                </div>
                <img
                    src="/images/industries/banking/payments.png"
                    alt="Payments"
                    className="rounded-2xl shadow-lg"
                />
            </section>

            {/* ================= FINTECH ================= */}
            <section className="py-24 bg-gray-50">
                <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-14 items-center">
                    <img
                        src="/images/industries/banking/fintech.png"
                        alt="FinTech"
                        className="rounded-2xl shadow-lg"
                    />
                    <div>
                        <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
                            <LineChart className="text-indigo-600" /> FinTech & ISVs
                        </h2>
                        <p className="text-gray-700 mb-6">
                            Accelerate fintech innovation with cloud-native platforms,
                            API-first architecture, and security-by-design.
                        </p>
                        <ul className="space-y-3">
                            <li className="flex gap-2"><CheckCircle2 className="text-green-600" /> API-first architecture</li>
                            <li className="flex gap-2"><CheckCircle2 className="text-green-600" /> Cloud-native engineering</li>
                            <li className="flex gap-2"><CheckCircle2 className="text-green-600" /> Faster product launches</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* ================= CAPABILITIES ================= */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-6">
                    <h2 className="text-3xl font-bold mb-12 text-center">
                        Our Banking Capabilities
                    </h2>
                    <div className="grid md:grid-cols-3 gap-8">
                        {industry.solutions.map((solution, i) => (
                            <div
                                key={i}
                                className="bg-white p-8 rounded-2xl shadow border hover:shadow-md transition"
                            >
                                <ShieldCheck className="text-indigo-600 mb-4" />
                                <h3 className="font-semibold">{solution}</h3>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ================= RESULTS ================= */}
            <section className="py-24 bg-green-50">
                <div className="max-w-7xl mx-auto px-6">
                    <h2 className="text-3xl font-bold mb-12 text-center">
                        Business Outcomes
                    </h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        {industry.results.map((result, i) => (
                            <div key={i} className="bg-white p-6 rounded-xl flex gap-3">
                                <CheckCircle2 className="text-green-600" />
                                <span>{result}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ================= CTA ================= */}
            <section className="bg-gradient-to-r from-indigo-600 to-purple-600 py-24 text-white text-center">
                <h2 className="text-3xl font-bold mb-4">
                    Transform Your Banking & Financial Services Business
                </h2>
                <p className="text-indigo-100 mb-8 max-w-2xl mx-auto">
                    Modernize platforms, accelerate innovation, and deliver secure,
                    customer-centric financial experiences.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button
                        asChild
                        size="lg"
                        className="bg-white text-indigo-600 hover:bg-gray-100 px-8 rounded-xl"
                    >
                        <Link to="/contact" className="flex items-center gap-2">
                            Contact Us <ArrowRight />
                        </Link>
                    </Button>

                    <Button
                        asChild
                        size="lg"
                        variant="outline"
                        className="border-white text-white hover:bg-white/10 px-8 rounded-xl"
                    >
                        <Link to="/client-success">View Success Stories</Link>
                    </Button>
                </div>
            </section>

        </div>
    );
};

export default BankingIndustryLayout;
